import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../cn';

const buttonStyles = cva(
  'inline-flex items-center justify-center gap-2 font-medium transition-all duration-150 ' +
    'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/35 ' +
    'disabled:cursor-not-allowed disabled:opacity-50 ' +
    'select-none whitespace-nowrap',
  {
    variants: {
      variant: {
        primary:
          'bg-brand-500 text-white shadow-soft hover:bg-brand-600 active:bg-brand-700 ' +
          'shadow-[inset_0_1px_0_0_rgb(255_255_255_/_0.15)]',
        secondary:
          'bg-ink-100 text-ink-800 hover:bg-ink-200 ' + 'border border-ink-200 shadow-soft',
        ghost: 'text-ink-700 hover:bg-ink-100 hover:text-ink-900',
        danger:
          'bg-danger-500 text-white shadow-soft hover:bg-danger-600 ' +
          'shadow-[inset_0_1px_0_0_rgb(255_255_255_/_0.15)]',
        outline:
          'border border-brand-500/40 text-brand-600 hover:bg-brand-50 hover:border-brand-500',
        // Variante para superfícies escuras (admin sidebar, etc)
        'on-dark': 'bg-white/8 text-white hover:bg-white/15 border border-white/10 backdrop-blur',
      },
      size: {
        sm: 'h-8 px-3 text-xs rounded-md',
        md: 'h-10 px-4 text-sm rounded-lg',
        lg: 'h-12 px-6 text-base rounded-lg',
        xl: 'h-14 px-8 text-base rounded-lg font-semibold',
        icon: 'h-10 w-10 rounded-lg',
      },
      block: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      block,
      loading,
      leadingIcon,
      trailingIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={cn(buttonStyles({ variant, size, block }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : leadingIcon}
        {children}
        {trailingIcon && !loading && trailingIcon}
      </button>
    );
  },
);
Button.displayName = 'Button';
